import React from 'react';

/**
 * InvestorsPage — /investors
 *
 * Follows the site-wide restyle pattern established in PlatformPage.jsx:
 *   - wrapper background #5b9bd5 (light blue)
 *   - Georgia / Times New Roman serif
 *   - ink #1e3a5f on white / translucent floating cards
 *   - MindStream colors used ONLY where they encode meaning (pillar row),
 *     never as decoration; Freedom/Happiness/Oneness inherit text color
 *
 * Route: <Route path="/investors" element={<InvestorsPage />} />
 *
 * CTA state (Jul 2026): no open demo yet, so the primary CTA anchors to
 * the walkthrough video and Schedule goes to a pre-filled mailto.
 * When the SAGE demo route ships:
 *   1. flip PRIMARY back to "Test the live platform" -> /demo
 *   2. restore the "Ours is a login." band copy
 */

const CONTACT_EMAIL = 'partnerships@freehappyone.com';
const SCHEDULE_MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('FHO Platform — investor access request')}`;

const ink = '#1e3a5f';
const serif = "'Georgia', 'Times New Roman', serif";

const styles = {
  page: {
    background: '#5b9bd5',
    minHeight: '100vh',
    fontFamily: serif,
    color: ink,
    padding: '0 0 80px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '18px 24px',
    maxWidth: '880px',
    margin: '0 auto',
  },
  container: {
    maxWidth: '880px',
    margin: '0 auto',
    padding: '0 24px',
  },
  card: {
    background: 'rgba(255, 255, 255, 0.96)',
    borderRadius: '14px',
    padding: '40px 44px',
    marginBottom: '28px',
    boxShadow: '0 2px 14px rgba(30, 58, 95, 0.12)',
  },
  eyebrow: {
    display: 'block',
    fontSize: '0.85rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    opacity: 0.65,
    marginBottom: '14px',
    fontWeight: 700,
  },
  h1: {
    fontSize: 'clamp(2rem, 5vw, 3rem)',
    lineHeight: 1.15,
    margin: '0 0 20px 0',
    fontWeight: 700,
  },
  h2: {
    fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
    lineHeight: 1.25,
    margin: '0 0 16px 0',
    fontWeight: 700,
  },
  lead: {
    fontSize: '1.2rem',
    lineHeight: 1.65,
    margin: '0 0 12px 0',
  },
  body: {
    fontSize: '1.08rem',
    lineHeight: 1.7,
    margin: '0 0 14px 0',
  },
  statRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '18px',
    marginTop: '26px',
  },
  stat: {
    flex: '1 1 150px',
    minWidth: '140px',
    background: 'rgba(240, 244, 248, 0.9)',
    borderRadius: '10px',
    padding: '18px 16px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '1.7rem',
    fontWeight: 700,
    display: 'block',
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: '0.88rem',
    opacity: 0.75,
    display: 'block',
    marginTop: '6px',
    lineHeight: 1.35,
  },
  ctaBand: {
    background: ink,
    color: '#F0F4F8',
    borderRadius: '14px',
    padding: '44px 44px',
    textAlign: 'center',
    marginBottom: '28px',
    boxShadow: '0 2px 14px rgba(30, 58, 95, 0.25)',
  },
  ctaButton: {
    display: 'inline-block',
    background: '#F0F4F8',
    color: ink,
    fontFamily: serif,
    fontWeight: 700,
    fontSize: '1.1rem',
    padding: '14px 34px',
    borderRadius: '10px',
    textDecoration: 'none',
    margin: '10px 8px 0 8px',
  },
  ctaButtonGhost: {
    display: 'inline-block',
    background: 'transparent',
    color: '#F0F4F8',
    border: '2px solid rgba(240, 244, 248, 0.7)',
    fontFamily: serif,
    fontWeight: 700,
    fontSize: '1.1rem',
    padding: '12px 32px',
    borderRadius: '10px',
    textDecoration: 'none',
    margin: '10px 8px 0 8px',
  },
  pillarRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '14px',
    marginTop: '10px',
  },
  pillar: (color) => ({
    flex: '1 1 220px',
    borderTop: `4px solid ${color}`,
    background: 'rgba(240, 244, 248, 0.9)',
    borderRadius: '0 0 10px 10px',
    padding: '16px 18px',
    fontSize: '1rem',
    lineHeight: 1.55,
  }),
  video: {
    width: '100%',
    display: 'block',
    borderRadius: '12px',
    marginTop: '8px',
    background: '#000',
  },
  footnote: {
    fontSize: '0.92rem',
    opacity: 0.75,
    lineHeight: 1.6,
    marginTop: '18px',
  },
};

const FHOLogo = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="18" cy="18" r="16" stroke="#059669" strokeWidth="2.5" fill="none"/>
    <path d="M18 8 A10 10 0 1 1 8 18" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <circle cx="18" cy="18" r="4" fill="#4f46e5"/>
  </svg>
);

export default function InvestorsPage() {
  return (
    <div style={styles.page}>

      {/* ── HEADER: route back to the main site ─────────── */}
      <header style={styles.header}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <FHOLogo size={34} />
          <span style={{ fontWeight: 700, color: '#fff', fontSize: '17px', letterSpacing: '0.02em' }}>
            <span style={{ color: '#4f46e5' }}>F</span>
            <span style={{ color: '#ef4444' }}>H</span>
            <span style={{ color: '#059669' }}>O</span>
            {' Platform'}
          </span>
        </a>
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
          Mind · Body · Spirit — wellness of the whole.
        </span>
      </header>

      <div style={styles.container}>

        {/* ── HERO ─────────────────────────────────────────── */}
        <section style={styles.card}>
          <span style={styles.eyebrow}>For Investors</span>
          <h1 style={styles.h1}>
            Built without a dollar of outside capital.
            Live in production. See it for yourself.
          </h1>
          <p style={styles.lead}>
            FHO is a faith-integrated AI wellness platform serving a segment
            most digital health products overlook: employees whose wellbeing
            runs through their faith. Six AI coaches, a proprietary
            methodology, and B2B distribution — running today at{' '}
            <a href="/" style={{ color: '#1e3a5f', fontWeight: 700 }}>freehappyone.com</a>.
          </p>
          <p style={styles.lead}>
            <em>FHO stands for Free Happy One — what we all strive to
            become.</em>
          </p>
          <div style={styles.statRow}>
            <div style={styles.stat}>
              <span style={styles.statValue}>$0</span>
              <span style={styles.statLabel}>debt &amp; outside capital</span>
            </div>
            <div style={styles.stat}>
              <span style={styles.statValue}>3,200+</span>
              <span style={styles.statLabel}>coaching sessions delivered</span>
            </div>
            <div style={styles.stat}>
              <span style={styles.statValue}>6</span>
              <span style={styles.statLabel}>AI coaches, one methodology</span>
            </div>
            <div style={styles.stat}>
              <span style={styles.statValue}>12</span>
              <span style={styles.statLabel}>partnership channels</span>
            </div>
          </div>
        </section>

        {/* ── BEAT 1: ENGINE AS COMMODITY ──────────────────── */}
        <section style={styles.card}>
          <span style={styles.eyebrow}>The engine</span>
          <h2 style={styles.h2}>
            We don't spend capital building a model. We spend it on
            distribution.
          </h2>
          <p style={styles.body}>
            FHO runs on frontier AI infrastructure — deliberately. No
            training capex, no model-maintenance burn, and every capability
            improvement in the underlying models flows to our coaches
            automatically. We have already migrated model generations in
            production without disruption.
          </p>
          <p style={styles.body}>
            The layer we own — the methodology, the knowledge base, the
            coaching architecture — is model-agnostic and portable. The
            engine is rented on purpose. Everything wrapped around it is
            ours.
          </p>
        </section>

        {/* ── BEAT 2: THE MOAT ─────────────────────────────── */}
        <section style={styles.card}>
          <span style={styles.eyebrow}>The moat</span>
          <h2 style={styles.h2}>
            Anyone can rent the same model. No one can rent our corpus, our
            segment, or our outcome data.
          </h2>
          <p style={styles.body}>
            <strong>Proprietary methodology.</strong> Every coach reasons
            inside Fhoism — an original, wholly-owned framework spanning a
            three-part manuscript, 12 Laws, 36 Dimensions, and the
            MindStreams model — grounded through retrieval so responses stay
            in-methodology, not generic.
          </p>
          <p style={styles.body}>
            <strong>A complete framework.</strong> Most philosophies address
            one dimension of human experience — what exists (ontology), how
            we know truth (epistemology), or how we should live (axiology).
            Fhoism addresses all three at once. That completeness is what
            makes it a genuine body of work rather than a wellness motif —
            and what makes it ownable.
          </p>
          <div style={styles.pillarRow}>
            <div style={styles.pillar('#4f46e5')}>
              <strong>Power</strong> — the Freedom stream: beliefs, meaning,
              and the eternal world.
            </div>
            <div style={styles.pillar('#ef4444')}>
              <strong>Flow</strong> — the Happiness stream: feelings,
              purpose, and the internal world.
            </div>
            <div style={styles.pillar('#059669')}>
              <strong>Clarity</strong> — the Oneness stream: actions, value,
              and the external world.
            </div>
          </div>
          <p style={{ ...styles.body, marginTop: '22px' }}>
            <strong>An underserved segment.</strong> Secular EAPs and
            wellness apps leave faith-active employees behind. Behavioral
            health investment is rotating toward exactly this kind of
            underserved population — and FHO's 12 partnership channels
            (health-cost sharing ministries, chaplaincy networks, PEOs,
            benefits brokers, HR, insurers, rehabilitation) are built to
            reach it where it already gathers.
          </p>
        </section>

        {/* ── BEAT 3: FUNDAMENTALS ─────────────────────────── */}
        <section style={styles.card}>
          <span style={styles.eyebrow}>The fundamentals</span>
          <h2 style={styles.h2}>
            Proof, not promises.
          </h2>
          <p style={styles.body}>
            FHO was designed, built, and deployed with no debt, no outside
            equity, no prior rounds to unwind. The full platform
            — six coaches, gamified journey engine, partner portal, quote
            and lead-generation tooling, and 12 live B2B channels — runs
            in production on near-zero burn.
          </p>
          <p style={styles.body}>
            That efficiency is the point: the product is already proven on
            almost nothing. Capital funds distribution and evidence.
          </p>
          <p style={styles.body}>
            <strong>Now raising a pre-seed round.</strong>
          </p>
        </section>

        {/* ── VIDEO: WALKTHROUGH ── */}
        <section id="walkthrough" style={styles.card}>
          <span style={styles.eyebrow}>Watch</span>
          <h2 style={styles.h2}>The walkthrough.</h2>
          <video controls preload="metadata" style={styles.video}>
            <source src="/videos/FHO_PreSeed_Deck.mp4?v=2" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </section>

        {/* ── CTA ──────────────────────────────────────────── */}
        <section style={styles.ctaBand}>
          <h2 style={{ ...styles.h2, color: '#F0F4F8' }}>
            Most pitches are decks. Ours is running.
          </h2>
          <p style={{ ...styles.body, color: '#F0F4F8', maxWidth: '560px', margin: '0 auto 6px auto' }}>
            Request access, and we'll put you inside
            the live platform.
          </p>
          <a href="/vc" style={styles.ctaButton}>
            Request platform access
          </a>
          <a href="/vc" style={styles.ctaButtonGhost}>
            Request a conversation
          </a>
          <p style={{ fontSize: '0.95rem', opacity: 0.8, marginTop: '18px' }}>
            Or write directly:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: '#F0F4F8' }}>
              {CONTACT_EMAIL}
            </a>
          </p>
        </section>

        <p style={styles.footnote}>
          FHO Platform is a product of MindStreams Technology (Thornbee LLC).
          Detailed materials — architecture, methodology IP, partnership
          pipeline, and financial structure — are available to qualified
          investors under NDA.
        </p>

      </div>
    </div>
  );
}
